import {
    AnalyticsLifecycleManager,
    Analytics as AnalyticsSDK,
} from '@superbet-group/web.analytics.sdk';
import { getConfig } from '@superbet-group/web.lib.config';
import {
    BonusType,
    BetSlipPurchaseType,
    AnalyticsAction,
} from '@superbet-group/web.types';
import {
    featureFlagService,
    maintenanceService,
    surveysFlagsService,
} from '@superbet-group/web.lib.flags';
import { sha256 } from '@src/app/utils/sha256';
import { formatRFC3339 } from 'date-fns';
import Vue from 'vue';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import router from '@app/router';
import { RouteName } from '@app/router/constants/RouteName';
import { isBehindLogin } from '@app/router/utils';
import { IState } from '@app/store';
import StoreUtil from '@app/store/StoreUtil';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import User from '@core/models/user/User';
import { PlayerDetailsFieldType } from '@core/models/user/enums';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import userDataService from '@core/services/user/UserDataService';
import { UserService } from '@core/services/user/UserService';
import RegisterInput from '@countryModels/authentication/RegisterInput';
import {
    getBalance,
    getWalletBreakdown,
    getBonusBalance,
    getBonusBalanceData,
    getTotalBonusBalance,
    getSessionId,
    getWithdrawableBalance,
} from '@models/user/UserDataService';
import {
    setHelperUserData,
    clearHelperUserData,
} from '@modules/help';
import {
    SET_HAS_FETCHED_COMMUNICATION_DATA,
    SET_IS_ACCEPTED_RESPONSIBLE_GAMBLING,
    SET_IS_AUTHENTICATING,
    SET_OMNI_WALLET_STATUS,
    SET_USER,
    SET_USER_DATA,
    SET_WITHDRAWAL_ABILITY,
    SET_IS_RETURNING_USER_DEVICE,
    SET_SESSION_START_TIME,
    SET_IS_CHECKED_AUTHENTICATION,
    SET_DEPOSIT_ELIGIBILITY,
    SET_FAILED_LOGIN_ATTEMPTS,
    SET_HAS_VISITED_ACCOUNT_SECURITY,
} from '@store/modules/data/authentication/mutationTypes';
import Limits from '@store/modules/limits/limitsIndex';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { asyncErrorGuard } from '@utils/ErrorGuard';
import { getDefaultMultiContext } from '@src/app/shared/featureFlags/initializeFeatureFlagMultiContext';
import { LDMultiKindContext } from 'launchdarkly-js-client-sdk';

import {
    createIsAuthenticated,
    createLogoutAction,
    createPostLogin,
    createPostLogout,
} from './actionCreators';
import {
    fetchUser,
    handleGeoLocationPingResult,
    initializeSessionHelper,
} from './helpers';
import {
    setSessionState,
    clearSessionState,
} from './session';
import type {
    AuthenticationGetters,
    Getters,
    IAuthenticationState,
    UserFieldMutationPayload,
} from './types';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { ModalData } from '@store/modules/ui/shared/modal/types';
import { omniWalletService } from '@src/app/utils/omniWalletHelper';
import PushNotificationsService from '@core/services/pushNotifications/PushNotificationsService';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import {
    addGeoLocationListener,
    pingLocation,
    removeGeoLocationListener,
    startPeriodicallyPinging,
    stopPeriodicallyPinging,
} from '@core/services/location/geoLocation';
import {
    Activity,
    ErrorPingResult,
} from '@core/services/location/types';
import { getIsUsingGeoLocationCheck } from '@core/services/location/helpers';
import {
    getMultiLoginDestination,
    isMultiLoginResponse,
} from '@app/multiLogin/helpers';
import { maybeMarkNewLogin } from '@app/multiLogin/isNewLogin';
import { LoginCredentialsWithMultiLoginDestination } from '@app/multiLogin/types';
import { locationObservability } from '@core/services/location/locationObservability';

export const DEFAULT_USER_BALANCE = '-';
export const LAST_SESSION_DURATION_SHOWN_DATE = 'LAST_SESSION_DURATION_SHOWN_DATE';
export const SESSION_START = 'users:sessionStart';

const pushNotificationsService = {
    connect(playerId: string, sessionId: string) {
        PushNotificationsService.getInstance().connect(playerId, sessionId);
    },
    disconnect() {
        PushNotificationsService.getInstance().disconnect();
    },
};

function getArePushNotificationEnabled(): Promise<boolean> {
    return Promise.resolve(getConfig().user.arePushNotificationsEnabled);
}

function rehydrateUser(storedData: Omit<User, 'registrationDate' | 'firstDepositDate' | 'userId'> & {
    registrationDate: string;
    firstDepositDate: string | boolean;
    userId: string;
}
    | null
    | undefined): User | null {
    let registrationDate: Date | null = null;
    let firstDepositDate: Date | boolean | null = null;
    let userId: number | null = null;

    if (!storedData) {
        return null;
    }
    const user: Omit<User, 'registrationDate' | 'firstDepositDate' | 'userId'> = { ...storedData };

    registrationDate = new Date(storedData.registrationDate);
    if (typeof storedData.firstDepositDate === 'boolean') {
        firstDepositDate = storedData.firstDepositDate;
    } else if (typeof storedData.firstDepositDate === 'string') {
        firstDepositDate = new Date(storedData.firstDepositDate);
    }

    if (storedData.userId) {
        userId = parseInt(storedData.userId);
        if (Number.isNaN(userId)) {
            userId = 0; // just for type safety. Hopefully won't happen
        }
    }
    return {
        ...user,
        userId: userId!,
        registrationDate: registrationDate!,
        firstDepositDate: firstDepositDate!,
    };
}

function defaultState(): IAuthenticationState {
    const user = rehydrateUser(clientStorageWithExpiry.get('user'));
    return {
        user,
        hasFetchedCommunicationData: false,
        lastSessionDurationShownDate: clientStorageWithExpiry.get<number>('user:lastSessionDurationShownDate')!,
        hasAcceptedResponsibleGambling: clientStorageWithExpiry.get<boolean>('user:hasAcceptedResponsibleGambling'),
        withdrawalAbility: null,
        isAuthenticating: false,
        isCheckedAuthentication: false,
        omniWalletStatus: null,
        isReturningUserDevice: !!clientStorageWithExpiry.get<boolean>('returningUserDevice'),
        sessionStartTime: clientStorageWithExpiry.get(SESSION_START),
        depositEligibility: null,
        failedLoginAttempts: 0,
        hasVisitedAccountSecurity: false,
    };
}

const getters: Getters = {
    user: StoreUtil.createSimpleGetter('user'),
    lastSessionDurationShownDate: StoreUtil.createSimpleGetter('lastSessionDurationShownDate'),
    hasAcceptedResponsibleGambling: StoreUtil.createSimpleGetter('hasAcceptedResponsibleGambling'),
    withdrawalAbility: StoreUtil.createSimpleGetter('withdrawalAbility'),
    isAuthenticating: StoreUtil.createSimpleGetter('isAuthenticating'),
    isCheckedAuthentication: StoreUtil.createSimpleGetter('isCheckedAuthentication'),
    isReturningUserDevice: StoreUtil.createSimpleGetter('isReturningUserDevice'),
    sessionStartTime: StoreUtil.createSimpleGetter('sessionStartTime'),
    depositEligibility: createSimpleGetter<IAuthenticationState, 'depositEligibility'>('depositEligibility'),
    userBonusBalanceData(state: IAuthenticationState) {
        return state.user ? getBonusBalanceData(state.user) : null;
    },
    userId(state: IAuthenticationState): number | null {
        return state.user?.userId ?? null;
    },
    omniWalletBalance(state: IAuthenticationState): number | null {
        return state.omniWalletStatus?.walletBalance ?? null;
    },
    cashbackBalance(state: IAuthenticationState): number | null {
        return state.omniWalletStatus?.cashbackBalance ?? null;
    },
    totalBonusBalance(state: IAuthenticationState) {
        return state.user ? getTotalBonusBalance(state.user) : 0;
    },
    userBalance(state: IAuthenticationState) {
        return state.user ? getBalance(state.user) : DEFAULT_USER_BALANCE;
    },
    withdrawableUserBalance(state: IAuthenticationState) {
        return state.user ? getWithdrawableBalance(state.user) : DEFAULT_USER_BALANCE;
    },
    walletBreakdown(state: IAuthenticationState) {
        return state.user ? getWalletBreakdown(state.user) : null;
    },
    sportBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.Sport) : 0;
    },
    freeBetBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.Freebet) : 0;
    },
    virtualsBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.Virtuals) : 0;
    },
    casinoVirtualsBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.CasinoVirtuals) : 0;
    },
    casinoBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.Casino) : 0;
    },
    freeSpinsBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.FreeSpins) : 0;
    },
    bingoBonusBalance(state: IAuthenticationState) {
        return state.user ? getBonusBalance(state.user, BonusType.Bingo) : 0;
    },
    userBalanceWithSportBonus(state: IAuthenticationState) {
        if (!state.user) {
            return null;
        }

        const sportBonus = getBonusBalance(state.user, BonusType.Sport);
        const balance = getBalance(state.user);
        return sportBonus !== null && balance !== null ? sportBonus + balance : null;
    },
    userHasMadeDeposit(state: IAuthenticationState) {
        return state.user?.hasMadeDeposit || false;
    },
    sessionId(state: IAuthenticationState): Nullable<string> {
        if (!state.user) {
            return null;
        }
        return getSessionId(state.user.sessionId);
    },
    sessionAndUserIds(state: IAuthenticationState): Nullable<string> {
        return state.user?.sessionId ?? null;
    },
    csrfToken(state: IAuthenticationState): Optional<string> {
        return state.user?.csrfToken;
    },
    isEligibleForWB(state: IAuthenticationState): boolean {
        return !!(state.user?.welcomeBonus && !state.user?.bonusAbuser);
    },
    failedLoginAttempts(state: IAuthenticationState): number {
        return state.failedLoginAttempts;
    },
    hasVisitedAccountSecurity(state: IAuthenticationState): boolean {
        return state.hasVisitedAccountSecurity;
    },
};

let userGeoLocationsListener: symbol | null = null;

const authenticationStore = {
    getters,

    namespaced: true,

    state: defaultState(),

    mutations: {
        [SET_USER]: (state: IAuthenticationState, user: User) => {
            clientStorageWithExpiry.set('user', user);
            state.user = user;
        },
        [SET_HAS_FETCHED_COMMUNICATION_DATA]: StoreUtil.createSimpleMutator('hasFetchedCommunicationData'),
        [SET_USER_DATA](state: IAuthenticationState, payload: UserFieldMutationPayload): void {
            Vue.set(state.user!, payload.key, payload.value);
            clientStorageWithExpiry.set('user', state.user);
        },
        [LAST_SESSION_DURATION_SHOWN_DATE]: StoreUtil.createSimpleMutator('lastSessionDurationShownDate'),
        [SET_IS_ACCEPTED_RESPONSIBLE_GAMBLING]: StoreUtil.createSimpleMutator('hasAcceptedResponsibleGambling'),
        [SET_WITHDRAWAL_ABILITY]: StoreUtil.createSimpleMutator('withdrawalAbility'),
        [SET_IS_AUTHENTICATING]: StoreUtil.createSimpleMutator('isAuthenticating'),
        [SET_IS_CHECKED_AUTHENTICATION]: StoreUtil.createSimpleMutator('isCheckedAuthentication'),
        [SET_OMNI_WALLET_STATUS]: StoreUtil.createSimpleMutator('omniWalletStatus'),
        [SET_IS_RETURNING_USER_DEVICE]: StoreUtil.createSimpleMutator('isReturningUserDevice'),
        [SET_SESSION_START_TIME]: StoreUtil.createSimpleMutator('sessionStartTime'),
        [SET_DEPOSIT_ELIGIBILITY]: createSimpleMutator<IAuthenticationState, 'depositEligibility'>('depositEligibility'),
        [SET_FAILED_LOGIN_ATTEMPTS](state: IAuthenticationState, value: number) {
            state.failedLoginAttempts = value;
            clientStorageWithExpiry.set('failedLoginAttempts', state.failedLoginAttempts);
        },
        [SET_HAS_VISITED_ACCOUNT_SECURITY](state: IAuthenticationState, value: boolean) {
            state.hasVisitedAccountSecurity = value;
            clientStorageWithExpiry.set('hasVisitedAccountSecurity', state.hasVisitedAccountSecurity);
        },
    },

    actions: {
        register: StoreUtil.asyncErrorGuard(async (
            context: ActionContext<IAuthenticationState, unknown>,
            registerInput: RegisterInput,
        ) => {
            if (await getIsUsingGeoLocationCheck()) {
                const geoLocationResult = await pingLocation(
                    await locationObservability.getIdentifier(registerInput.username),
                    Activity.Registration,
                );
                if (handleGeoLocationPingResult(geoLocationResult, context.dispatch)) {
                    throw new Error('geoLocationError');
                }
            }
            return authenticationService.register(registerInput);
        }),

        login: StoreUtil.asyncErrorGuard(async (
            { dispatch, rootGetters }: ActionContext<IAuthenticationState, unknown>,
            { username, password, multiLoginDestination, otpId, reopen, otp, action }: LoginCredentialsWithMultiLoginDestination,
        ) => {
            if (await getIsUsingGeoLocationCheck()) {
                const geoLocationResult = await pingLocation(
                    (await locationObservability.getIdentifier(username)),
                    Activity.Login,
                );
                if (handleGeoLocationPingResult(geoLocationResult, dispatch, false)) {
                    return;
                }
            }

            const multiLoginDestinationHref = multiLoginDestination
                || rootGetters['navigation/loginGuardedHref']
                || window.location.href;

            const isJwtOnLoginEnabled = await featureFlagService.getFeatureValue('auth.request-jwt-on-login', false);

            const user = await authenticationService.login(
                {
                    username,
                    password,
                    ...(otpId && { otpId }),
                    ...(action && { action }),
                    ...(reopen && { reopen }),
                    ...(otp && { otp }),
                    ...(isJwtOnLoginEnabled && { includeAccessToken: true }),
                },
                getMultiLoginDestination(new URL(multiLoginDestinationHref)),
            );

            if (await getIsUsingGeoLocationCheck()) {
                await locationObservability.setIdentifier(username, user.userUuid!);
            }
            maybeMarkNewLogin();

            async function completeLogin() {
                const hashEmail = await sha256(user.email);

                AnalyticsLifecycleManager
                    .getInstance()
                    .setUserData({
                        userId: user.userId?.toString(),
                        userUuid: user.userUuid?.toString(),
                        userEmail: user.email,
                    });

                AnalyticsSDK.getInstance().log(
                    'Login_Successful',
                    {
                        loginMethod: AnalyticsAction.Classic,
                        hashEmail,
                        userId: user.userId.toString(),
                    },
                );

                if (isMultiLoginResponse(user)) {
                    window.location.assign(user.redirect);
                    return;
                }

                await dispatch('postLogin', { user: await userDataService.getUserData(user), isLogin: true });
                dispatch('setFailedLoginAttempts', 0);
                dispatch('ui/sportOffer/betSlip/setPurchaseType', BetSlipPurchaseType.online, { root: true });
            }

            if (user.mfaPayload) {
                dispatch('ui/modal/setModal', {
                    code: modalCodes.mfaCode,
                    data: {
                        mfaPayload: user.mfaPayload,
                        onSuccess: (closeModal: () => Promise<void>) => {
                            closeModal();
                            completeLogin();
                        },
                    },
                }, { root: true });
            } else {
                completeLogin();
            }
        }),

        getCommunicationDetails: StoreUtil.asyncErrorGuard(
            async (context: ActionContext<IAuthenticationState, IState>) => {
                if (context.state.hasFetchedCommunicationData) {
                    return;
                }

                const communication = await UserService.getInstance()
                    .getUserDetails([
                        PlayerDetailsFieldType.phone,
                        PlayerDetailsFieldType.communication,
                    ]);

                const updateUser = {
                    ...context.state.user,
                    ...communication,
                };
                context.commit(SET_USER, updateUser);
                context.commit(SET_HAS_FETCHED_COMMUNICATION_DATA, true);
            }),

        postLogin: createPostLogin({
            pushNotificationsService,
            getArePushNotificationEnabled,
            saveSessionStart: function (context: ActionContext<IAuthenticationState, IState>, timestamp = Date.now()) {
                context.dispatch('setSessionStartTime', timestamp);
            },
            setIsReturningUserDevice: function (context : ActionContext<IAuthenticationState, IState>) {
                context.dispatch('setIsReturningUserDevice');
            },
            setHelperUserData,
            setSessionState,
            scheduleGeoLocationPing: async function (user: User, context: ActionContext<IAuthenticationState, IState>) {
                userGeoLocationsListener = addGeoLocationListener({
                    onAllow: () => {},
                    onBlock: (pingResult) => {
                        context.dispatch('logout');
                        context.dispatch(
                            'ui/modal/setModal',
                            {
                                code: modalCodes.geoLocationCheck,
                                data: {
                                    code: (pingResult as ErrorPingResult).code,
                                    clientId: (pingResult as ErrorPingResult).clientId,
                                },
                            },
                            { root: true },
                        );
                    },
                    onUnavailable: () => {
                        // TODO: double check this
                    },
                    onPermissionPrompt: () => {},
                });
                startPeriodicallyPinging((await locationObservability.getIdentifier(user.username))!);
            },
            initializePersonalisedConfig: function (user: User) {
                const keyVarName = getConfig().countrySpecific.launchDarkly.userKeyVariable;
                const key = `${user[keyVarName]}`;

                let context: LDMultiKindContext;
                if (key) {
                    context = {
                        ...getDefaultMultiContext(),
                        user: {
                            key,
                            registration_date: formatRFC3339(user.registrationDate),
                        },
                    };
                } else {
                    context = getDefaultMultiContext();
                }
                if (surveysFlagsService) {
                    surveysFlagsService.initializeWithMultiContext(context);
                }
                featureFlagService.initializeWithMultiContext(context);
                maintenanceService.setContext(context);
            },
        }),

        clearSessionState: (context: ActionContext<IAuthenticationState, IState>) => clearSessionState(context),
        setSessionState: (context: ActionContext<IAuthenticationState, IState>, user: User) => setSessionState(user, context),
        isAuthenticated: StoreUtil.asyncErrorGuard(createIsAuthenticated(
            clearSessionState,
            authenticationService,
        )),

        initializeSession: StoreUtil.asyncErrorGuard((context: ActionContext<IAuthenticationState, IState>) => {
            return initializeSessionHelper({
                cachedUser: context.state.user,
                context,
                clearSession: clearSessionState,
                fetchUser: () => {
                    return fetchUser({
                        cachedUser: context.state.user,
                        fetchUserDetails: UserService.getInstance().getUserDetails.bind(UserService.getInstance()),
                    });
                },
                userData: userDataService.getUserData.bind(userDataService),
            });
        }),

        responsibleGamblingSessionCheckModal({ dispatch }: { dispatch: Dispatch }): void {
            dispatch(
                'ui/modal/setModal',
                {
                    code: modalCodes.responsibleGamblingModal,
                    goBack: false,
                    isClosable: false,
                },
                { root: true },
            );
        },

        logout: asyncErrorGuard(createLogoutAction(authenticationService)),

        postLogout: createPostLogout({
            getArePushNotificationEnabled,
            clearSessionState,
            pushNotificationsService,
            clearHelperUserData,
            behindLoginAction({ rootGetters }: { rootGetters: any }) {
                const currentRoute = rootGetters['navigation/route'];
                if (isBehindLogin(currentRoute)) {
                    router.push({
                        name: RouteName.App.Home,
                    });
                }
            },
            initializeAnonymousUserConfig: () => {
                featureFlagService.initializeWithMultiContext(getDefaultMultiContext());
                maintenanceService.unsetUser();
            },
            clearUserLocationListener: () => {
                if (userGeoLocationsListener) {
                    removeGeoLocationListener(userGeoLocationsListener);
                    userGeoLocationsListener = null;
                }
                stopPeriodicallyPinging();
            },
        }),

        /**
         * Session duration is currently set to 10800 seconds (3 hours). Session is extended on every secure
         * api call (player session is provided on the call) if the difference between initial session's
         * expiry date (login time + 3hours) and current time + 3 hours is bigger than 18 minutes
         * so if player logs in at 9:30, estimated expiry would be 12:30.
         * If secure action is done at 9:40, difference between 12:30 and 12:40 is 10 minutes
         * which is smaller than 18 minutes, session is not prolonged
         * If first secure action is done at 9:50, difference between 12:30 and 12:50 is 20minutes,
         * which is bigger than 18 minutes, session is prolonged to 12:50 (current secure action time + 3 hours)
         *
         * So basically, after login, at least 18minutes need to pass in order for the session
         * to be prolonged for another 3 hours
         */
        sessionExpired({ dispatch }: { dispatch: Dispatch }, content?: string): void {
            dispatch('ui/modal/closeModal', null, { root: true });
            dispatch('logout', false);

            if (shellLocalConfig.shouldShowSessionExpireModal) {
                dispatch(
                    'ui/modal/setModal',
                    {
                        code: modalCodes.error,
                        title: 'pam.session.sessionEnded.title',
                        containerClass: 'modal--native-design session-ended',
                        isClosable: false,
                        data: {
                            kind: ModalData.SessionEnded,
                            illustration: '/static/img/illustrations/time__warning.svg',
                            text: {
                                content: content || 'pam.session.sessionEnded.text',
                                className: 'instruction',
                            },
                            button: {
                                label: 'close',
                            },
                        },
                    },
                    { root: true },
                );
            }
        },

        activateAccount: StoreUtil.asyncErrorGuard(
            async (context: ActionContext<IAuthenticationState, IState>, token: string) => {
                const user = await authenticationService.activateAccount(
                    token,
                    getMultiLoginDestination(new URL(window.location.origin)),
                );
                maybeMarkNewLogin();
                if (isMultiLoginResponse(user)) {
                    window.location.assign(user.redirect);
                    return;
                }

                if (user) {
                    user.balance = await UserService.getInstance().getPlayerBalance();
                    const details = await UserService.getInstance().getUserDetails();
                    Object.assign(user, details);
                    user.welcomeBonus = getConfig().countrySpecific.user.bonus.isWelcomeBonusShown;

                    // const hashEmail = await sha256(user.email);

                    AnalyticsLifecycleManager
                        .getInstance().setUserData({
                            userId: user.userId?.toString(),
                            userUuid: user.userUuid?.toString(),
                            userEmail: user.email,
                        });

                    context.dispatch('postLogin', {
                        user: await userDataService.getUserData(user),
                        isLogin: true,
                    });

                    context.dispatch('ui/sportOffer/betSlip/setPurchaseType', BetSlipPurchaseType.online, { root: true });

                    context.dispatch('social/auth/resolveJoinSocialIntent', user, { root: true });
                }
            }),

        setUserData({ commit }: { commit: Commit }, { key, value }: { key: string; value: any }): void {
            commit(SET_USER_DATA, { key, value });
        },

        updateUserDetails(context: ActionContext<IAuthenticationState, IState>, next: Partial<User>): void {
            const updatedUser = {
                ...context.state.user,
                ...next,
            };

            context.commit(SET_USER, updatedUser);
        },

        setSessionStartTime({ commit }: { commit: Commit }, timestamp = Date.now()): void {
            clientStorageWithExpiry.set(SESSION_START, timestamp);
            commit(SET_SESSION_START_TIME, timestamp);
        },
        sendActivationEmail: asyncErrorGuard(async (_, { email }: { email: string }) => {
            await UserService.getInstance().sendActivationEmail(email);
        }),
        fetchWithdrawalAbility: StoreUtil.asyncErrorGuard(async ({ commit }: ActionContext<IAuthenticationState, IState>) => {
            const withdrawalAbility = await UserService.getInstance().checkWithdrawalAbility();
            commit(SET_WITHDRAWAL_ABILITY, withdrawalAbility);
        }),

        setIsAuthenticating: (context: ActionContext<IAuthenticationState, IState>, isAuthenticating: boolean) => {
            context.commit(SET_IS_AUTHENTICATING, isAuthenticating);
        },

        setIsCheckedAuthentication: (context: ActionContext<IAuthenticationState, IState>, isCheckedAuthentication: boolean) => {
            context.commit(SET_IS_CHECKED_AUTHENTICATION, isCheckedAuthentication);
        },

        acceptResponsibleGambling: (context: ActionContext<IAuthenticationState, IState>) => {
            clientStorageWithExpiry.set('user:hasAcceptedResponsibleGambling', true);
            context.commit(SET_IS_ACCEPTED_RESPONSIBLE_GAMBLING, true);
        },

        setIsReturningUserDevice: (context: ActionContext<IAuthenticationState, IState>) => {
            clientStorageWithExpiry.set('returningUserDevice', true, 3 * 365);
            context.commit(SET_IS_RETURNING_USER_DEVICE, true);
        },

        getOmniWalletBalance: async (context: ActionContext<IAuthenticationState, unknown>, retailPlayerId: number) => {
            const omniWalletStatus = await omniWalletService.getUserWallet(retailPlayerId);
            context.commit(SET_OMNI_WALLET_STATUS, omniWalletStatus);
        },

        fetchDepositEligibility: asyncErrorGuard(async ({ commit }: ActionContext<IAuthenticationState, IState>) => {
            const depositEligibility = await UserService.getInstance().checkDepositEligibility();
            commit(SET_DEPOSIT_ELIGIBILITY, depositEligibility);
        }),
        setFailedLoginAttempts(context: ActionContext<IAuthenticationState, IState>, value: number) {
            context.commit(SET_FAILED_LOGIN_ATTEMPTS, value);
        },
        setHasVisitedAccountSecurity(context: ActionContext<IAuthenticationState, IState>, value: boolean) {
            context.commit(SET_HAS_VISITED_ACCOUNT_SECURITY, value);
        },
    },

    modules: {
        Limits,
    },
};

export default authenticationStore;

type AuthenticationStore = typeof authenticationStore & typeof Limits;

export {
    IAuthenticationState,
    AuthenticationGetters,
    AuthenticationStore,
};
